/**
 * 纯 TS 的 SHA-256 实现。
 *
 * 用途：PKCE code_challenge 需要 SHA-256，但 `crypto.subtle` 仅在安全上下文
 * （HTTPS 或 localhost）可用；通过 http://<IP>:<port> 访问时浏览器不提供
 * crypto.subtle，登录会报「Cannot read properties of undefined (reading 'digest')」。
 * 此时回退到本实现。
 *
 * K/H 常量按标准由素数的立方根/平方根小数部分现算，避免手抄 64 个魔数出错。
 * 正确性由测试用例以 RFC 4634/7636 测试向量锁定。
 */

let cachedK: Uint32Array | null = null;
let cachedH0: Uint32Array | null = null;

/** 取 K（前 64 个素数立方根小数部分）与 H0（前 8 个素数平方根小数部分）。 */
function constants(): { K: Uint32Array; H0: Uint32Array } {
  if (cachedK && cachedH0) return { K: cachedK, H0: cachedH0 };
  const K = new Uint32Array(64);
  const H0 = new Uint32Array(8);
  let k = 0;
  let h = 0;
  outer: for (let candidate = 2; k < 64 || h < 8; candidate++) {
    for (let i = 2; i * i <= candidate; i++) {
      if (candidate % i === 0) continue outer;
    }
    // candidate 是素数
    if (h < 8) H0[h++] = Math.floor((Math.sqrt(candidate) % 1) * 0x100000000) >>> 0;
    if (k < 64) K[k++] = Math.floor((Math.cbrt(candidate) % 1) * 0x100000000) >>> 0;
  }
  cachedK = K;
  cachedH0 = H0;
  return { K, H0 };
}

function rotr(x: number, n: number): number {
  return ((x >>> n) | (x << (32 - n))) >>> 0;
}

/** 计算字节的 SHA-256 摘要，返回 32 字节。 */
export function sha256Bytes(data: Uint8Array): Uint8Array {
  const { K, H0 } = constants();
  const H = new Uint32Array(H0);

  // 补齐：原消息 + 0x80 + 0 填充 + 8 字节大端位长度
  const bitLen = data.length * 8;
  const paddedLen = ((data.length + 1 + 8 + 63) >> 6) << 6;
  const msg = new Uint8Array(paddedLen);
  msg.set(data);
  msg[data.length] = 0x80;
  const view = new DataView(msg.buffer);
  view.setUint32(paddedLen - 8, Math.floor(bitLen / 0x100000000));
  view.setUint32(paddedLen - 4, bitLen >>> 0);

  const w = new Uint32Array(64);
  for (let block = 0; block < paddedLen; block += 64) {
    for (let t = 0; t < 16; t++) w[t] = view.getUint32(block + t * 4);
    for (let t = 16; t < 64; t++) {
      const s0 = rotr(w[t - 15], 7) ^ rotr(w[t - 15], 18) ^ (w[t - 15] >>> 3);
      const s1 = rotr(w[t - 2], 17) ^ rotr(w[t - 2], 19) ^ (w[t - 2] >>> 10);
      w[t] = (w[t - 16] + s0 + w[t - 7] + s1) >>> 0;
    }
    let a = H[0], b = H[1], c = H[2], d = H[3];
    let e = H[4], f = H[5], g = H[6], h = H[7];
    for (let t = 0; t < 64; t++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[t] + w[t]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0] + a) >>> 0;
    H[1] = (H[1] + b) >>> 0;
    H[2] = (H[2] + c) >>> 0;
    H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0;
    H[5] = (H[5] + f) >>> 0;
    H[6] = (H[6] + g) >>> 0;
    H[7] = (H[7] + h) >>> 0;
  }

  const out = new Uint8Array(32);
  const ov = new DataView(out.buffer);
  for (let i = 0; i < 8; i++) ov.setUint32(i * 4, H[i]);
  return out;
}
